import React from 'react'
import { classNames } from 'common/emotion'
import { addListener, removeListener } from 'component-library/__helpers/global-dom-events'
import {
    calculateMargin,
    getUnitVisibilityBreakpoints,
} from 'component-library/advertising/AdUnit/util'

import {
    StyledCenter,
    StyledNotice,
    AdNoticePosition,
    StyledAdUnitWrapper,
} from 'component-library/advertising/AdUnit/AdUnit.styled'

import { useFeature } from 'common/feature-togglings/features'
import { AdState } from 'common/advertising/ad-state'
import { isRichMediaAd } from './isRichMediaAd'
import { ViewabilityAdUnit } from './ViewabilityAdUnit'
import { AdEvent } from 'common/events/viewability-event'
import { SlotRenderEndedEvent } from '../gpt/GptAdProvider/components/GptManager'

export interface Breakout {
    kind: 'absolute' | 'percentage'
    value: number
    breakpoint: number
}

/**
 * TODO investigate moving placeholder height from ../ad-helpers/
 */
export interface AdUnitWrapperProps {
    /**
     * Position of the notice that this unit is an ad should be displayed
     */
    noticePosition: AdNoticePosition

    /**
     * Padding to be applied to the ad unit
     * not applied if ad unit is hidden
     */
    padding?: [number] | [number, number] | [number, number, number, number]
    /**
     * Ignores the placeholder height
     */
    hiddenUntilLoaded?: boolean

    breakout?: Breakout
    removeAdContainerStyles?: boolean

    /** Called once the ad unit has rendered */
    unitRendered?: () => void
    stickyOffset?: number
}

export interface AdUnitWrapperInternalProps extends AdUnitWrapperProps {
    onEvent: (event: AdEvent) => void
}

export interface AdSlotProps {
    unitId: string
    adState: AdState
}

export type Props = AdUnitWrapperInternalProps & AdSlotProps

/**
 * This component is the outer most ad unit component
 *
 * It is responsible for collapsing the ad unit if an ad is not rendered and rendering the ad notice
 */
export const AdUnitWrapper: React.FC<Props> = ({
    hiddenUntilLoaded,
    adState,
    unitId,
    breakout,
    unitRendered,
    onEvent,
    noticePosition,
    padding,
    stickyOffset,
}) => {
    const breakoutContainerRef = React.useRef<HTMLDivElement | null>(null)

    const [shouldHide, setShouldHide] = React.useState(hiddenUntilLoaded || false)
    const [breakoutMargins, setBreakoutMargins] = React.useState({ left: 0, right: 0 })
    const [removeAdContainerStyles, setRemoveAdContainerStyles] = React.useState(false)
    const [recievedAdSlot, setRecievedAdSlot] = React.useState<boolean | undefined>(undefined)
    const [previousAdPageKey, setPreviousAdPageKey] = React.useState(adState.pageKey)

    const isStickyEnabled = useFeature('sticky-mrec', false)

    // If we recieve updated props where the adPageKey does not match the next adPageKey:
    // TODO this won't work for th 5minute page reload behaviour
    // unless we are actually refreshing the page
    if (previousAdPageKey !== adState.pageKey) {
        setPreviousAdPageKey(adState.pageKey)
        setShouldHide(hiddenUntilLoaded || false)
        setRecievedAdSlot(undefined)
    }

    const onSlotMutation = React.useCallback(() => setRemoveAdContainerStyles(isRichMediaAd()), [])

    const calculateBreakoutMargins = React.useCallback(() => {
        if (!breakout || !breakoutContainerRef.current || !document.documentElement) {
            return
        }

        const viewportWidth = document.documentElement.clientWidth
        const containerWidth = breakoutContainerRef.current.getBoundingClientRect().width
        const { kind, value, breakpoint } = breakout

        let finalMargin = 0
        switch (kind) {
            case 'percentage': {
                const absMargin = Math.ceil(containerWidth * (value / 100))
                finalMargin = calculateMargin(absMargin, containerWidth, breakpoint, viewportWidth)
                break
            }
            default:
            case 'absolute':
                finalMargin = calculateMargin(value, containerWidth, breakpoint, viewportWidth)
                break
        }

        setBreakoutMargins({
            left: finalMargin,
            right: finalMargin,
        })

        /* eslint-disable react-hooks/exhaustive-deps */
        // eslint complains that we can't use expressions but we need to as otherwise
        // js would always interperate `breakout` as a new object
    }, [breakout && breakout.breakpoint, breakout && breakout.kind, breakout && breakout.value])
    /* eslint-enable react-hooks/exhaustive-deps */

    React.useEffect(() => {
        if (!breakoutContainerRef.current) return
        calculateBreakoutMargins()
        addListener('resize', calculateBreakoutMargins)
        return () => removeListener('resize', calculateBreakoutMargins)
        // only re-compute if calculateBreakoutMargins changes, this is important as calculateBreakoutMargins sets state,
        // and otherwise may introduce infinite re-rendering
    }, [calculateBreakoutMargins])

    const onSlotRenderEnded = React.useCallback(
        (event: SlotRenderEndedEvent) => {
            const hasNoPlaceholderHeight = event.unit && !event.unit.placeholderHeight
            const canCollapse = hasNoPlaceholderHeight || hiddenUntilLoaded !== undefined

            setShouldHide(canCollapse && event.isEmpty)
            setRecievedAdSlot(!event.isEmpty)
            setRemoveAdContainerStyles(isRichMediaAd())

            if (unitRendered) {
                unitRendered()
            }
        },
        [unitRendered, hiddenUntilLoaded]
    )

    const visibilityBreakpoints = getUnitVisibilityBreakpoints(
        { unitId, adState },
        recievedAdSlot !== true
    )

    if (noticePosition === 'none') {
        return (
            <StyledAdUnitWrapper
                className={classNames('hide-print', 'ad-no-notice')}
                stickyOffset={isStickyEnabled ? stickyOffset : undefined}
                ref={breakoutContainerRef}
            >
                <StyledCenter
                    visibilityBreakpoints={visibilityBreakpoints}
                    hidden={shouldHide}
                    padding={padding}
                >
                    <ViewabilityAdUnit
                        unitId={unitId}
                        adState={adState}
                        removeAdContainerStyles={removeAdContainerStyles}
                        onSlotMutation={onSlotMutation}
                        onSlotRenderEnded={onSlotRenderEnded}
                        breakoutContainerRef={breakoutContainerRef}
                        breakoutMargins={breakoutMargins}
                        onEvent={onEvent}
                        recievedAdSlot={recievedAdSlot}
                        shouldHide={shouldHide}
                        visibilityBreakpoints={visibilityBreakpoints}
                    />
                </StyledCenter>
            </StyledAdUnitWrapper>
        )
    }

    return (
        <div className={classNames('hide-print', 'ad-with-notice')} ref={breakoutContainerRef}>
            <StyledCenter
                visibilityBreakpoints={visibilityBreakpoints}
                hidden={shouldHide}
                padding={padding}
            >
                <StyledNotice
                    noticePosition={noticePosition}
                    noticeMessage={recievedAdSlot === true ? 'Advertisement' : ' '}
                    removeAdContainerStyles={removeAdContainerStyles}
                >
                    <ViewabilityAdUnit
                        unitId={unitId}
                        adState={adState}
                        removeAdContainerStyles={removeAdContainerStyles}
                        onSlotMutation={onSlotMutation}
                        onSlotRenderEnded={onSlotRenderEnded}
                        breakoutContainerRef={breakoutContainerRef}
                        breakoutMargins={breakoutMargins}
                        onEvent={onEvent}
                        recievedAdSlot={recievedAdSlot}
                        shouldHide={shouldHide}
                        visibilityBreakpoints={visibilityBreakpoints}
                    />
                </StyledNotice>
            </StyledCenter>
        </div>
    )
}
